import { forwardRef } from '@chakra-ui/react'
import { DotPhraseTemplate, WithId } from '@hb/shared'
import { ComponentProps, RefObject, useCallback, useMemo, useRef, useState } from 'react'
import TextAreaAutosize from 'react-textarea-autosize'
import { useDotPhrase } from '../../../../../contexts/dotPhrase'
import { getCaretViewportPosition } from '../caretCoordinates'
import { DotPhrasePicker } from '../DotPhrasePicker'
import { getDotPhraseToken } from '../styles'

export const TextAreaElement = forwardRef<
  ComponentProps<typeof TextAreaAutosize> & { withDotPhrases?: boolean },
  'textarea'
>(({ style, onKeyDown, onChange, onBlur, withDotPhrases, ...props }, ref) => {
  // Dot phrases are opt-in per field — only active when the field sets `withDotPhrases`.
  const dotPhraseCtx = useDotPhrase()
  const dotPhrase = withDotPhrases ? dotPhraseCtx : null
  const [showPicker, setShowPicker] = useState(false)
  const [phraseSearch, setPhraseSearch] = useState('')
  // Keyboard-highlighted row in the picker.
  const [activeIndex, setActiveIndex] = useState(0)
  // Viewport coords of the caret, so the picker can float next to the cursor.
  const [caretPos, setCaretPos] = useState<{
    top: number
    left: number
  } | null>(null)
  // Internal ref to the underlying textarea DOM element for picker selection.
  const taRef = useRef<HTMLTextAreaElement | null>(null)

  // Read latest picker state inside handlers without depending on it, so the handlers stay
  // stable and make zero state updates while typing ordinary text.
  const showPickerRef = useRef(showPicker)
  showPickerRef.current = showPicker
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  const pickerItemsRef = useRef<WithId<DotPhraseTemplate>[]>([])

  // Merge forwarded ref with our internal ref via a callback.
  const mergedRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      taRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) (ref as RefObject<HTMLTextAreaElement | null>).current = el
    },
    [ref],
  )

  const closePicker = useCallback(() => {
    setShowPicker(false)
    setPhraseSearch('')
    setCaretPos(null)
    setActiveIndex(0)
  }, [])

  const handlePickerSelect = useCallback(
    (body: string) => {
      const el = taRef.current
      if (!el) return
      // Replace only the ".keyword" token at the caret with the template body.
      const caret = el.selectionStart ?? el.value.length
      const token = getDotPhraseToken(el.value, caret)
      const tokenStart = token ? token.tokenStart : caret
      const newValue = el.value.slice(0, tokenStart) + body + el.value.slice(caret)
      const newCaret = tokenStart + body.length
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
      setter?.call(el, newValue)
      el.dispatchEvent(new Event('input', { bubbles: true }))
      requestAnimationFrame(() => el.setSelectionRange(newCaret, newCaret))
      closePicker()
    },
    [closePicker],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // While the picker is open, take over navigation keys (Tab/Enter no longer leave the
      // field or insert a newline).
      const items = pickerItemsRef.current
      if (dotPhrase && showPickerRef.current && items.length > 0) {
        const n = items.length
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setActiveIndex(i => (i + 1) % n)
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setActiveIndex(i => (i - 1 + n) % n)
          return
        }
        if (e.key === 'Tab') {
          e.preventDefault()
          setActiveIndex(i => (i + (e.shiftKey ? -1 : 1) + n) % n)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          const item = items[Math.min(activeIndexRef.current, n - 1)]
          if (item) handlePickerSelect(item.body)
          return
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          closePicker()
          return
        }
      }
      // Picker closed: Tab expands the keyword before the caret (existing behavior).
      if (e.key === 'Tab' && dotPhrase && e.currentTarget) {
        const el = e.currentTarget
        const result = dotPhrase.tryExpand(el.value, el.selectionStart ?? 0, props.name ?? '')
        if (result.matched) {
          e.preventDefault()
          const setter = Object.getOwnPropertyDescriptor(
            HTMLTextAreaElement.prototype,
            'value',
          )?.set
          setter?.call(el, result.newValue)
          el.dispatchEvent(new Event('input', { bubbles: true }))
          const cursor = result.newCursor
          requestAnimationFrame(() => el.setSelectionRange(cursor, cursor))
          closePicker()
        }
      }
      onKeyDown?.(e as any)
    },
    [dotPhrase, onKeyDown, props.name, handlePickerSelect, closePicker],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e as any)
      if (!dotPhrase) return
      const el = e.target
      const caret = el.selectionStart ?? el.value.length
      // Trigger on a '.' that starts a word anywhere in the field (e.g. ".hpi" mid-note),
      // not only when the whole field is exactly ".". No state update in the common case.
      const token = getDotPhraseToken(el.value, caret)
      if (token) {
        if (!showPickerRef.current) setShowPicker(true)
        setPhraseSearch(token.keyword)
        setActiveIndex(0) // highlight the top match as the list refilters
        // Anchor the picker just below the caret (top of the '.' token reads best).
        setCaretPos(getCaretViewportPosition(el, token.tokenStart))
      } else if (showPickerRef.current) {
        closePicker()
      }
    },
    [dotPhrase, onChange, closePicker],
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      closePicker()
      onBlur?.(e as any)
    },
    [onBlur, closePicker],
  )

  const pickerItems = useMemo(() => {
    if (!dotPhrase || !showPicker) return []
    return dotPhrase.templates
      .filter(
        t =>
          !phraseSearch ||
          t.keyword.toLowerCase().includes(phraseSearch.toLowerCase()) ||
          t.description?.toLowerCase().includes(phraseSearch.toLowerCase()),
      )
      .slice(0, 8)
  }, [dotPhrase, showPicker, phraseSearch])
  pickerItemsRef.current = pickerItems

  const taStyle = useMemo(
    () => ({
      resize: 'none' as const,
      width: '100%',
      // Padding + border/radius are owned by the InputContainer; the textarea sits flush.
      padding: 0,
      fontFamily: 'hero-new',
      boxSizing: 'border-box' as const,
      // 12px body text / #333 — spec Section 0.6.
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: props.disabled ? '#718096' : '#333333',
      background: 'none',
      border: 'none',
      outline: 'none',
      ...style,
    }),
    [props.disabled, style],
  )

  const textArea = (
    <TextAreaAutosize
      className="hb-textarea"
      ref={dotPhrase ? (mergedRef as any) : ref}
      style={taStyle}
      onKeyDown={handleKeyDown as any}
      onChange={handleChange as any}
      onBlur={handleBlur as any}
      {...props}
    />
  )

  // Keep the textarea at a STABLE tree position and toggle the picker as a sibling. Wrapping/
  // unwrapping the textarea would remount it, dropping focus and firing blur — which closed the
  // picker the instant '.' was typed. The picker itself floats at the caret (position: fixed).
  if (!dotPhrase) return textArea

  return (
    <>
      {textArea}
      {showPicker && pickerItems.length > 0 && (
        <DotPhrasePicker
          items={pickerItems}
          onSelect={handlePickerSelect}
          coords={caretPos}
          activeIndex={Math.min(activeIndex, pickerItems.length - 1)}
          onHoverIndex={setActiveIndex}
        />
      )}
    </>
  )
})
